/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import produce from 'immer';
import {GenericObject} from '@ion/core/src/types/entities';
import {
  EXPORT_COMPLETED,
  EXPORT_CONFIRMED,
  EXPORT_ERROR,
  EXPORT_REQUEST,
  EXPORT_RESPONSE
} from '../actions/exporter';
import {ExportContext} from './../../types/redux';

export interface ExportAction {
  entity: ExportContext,
  exportLimit: number
}

export interface EntityExportState {
  pending: {},
  entity: ExportContext | null;
  isExporting: boolean;
  isComplete: boolean;
  isError: boolean;
  show: boolean;
  exportLimit: number;
};

export interface ExportState {
  current: EntityExportState,
}

const defaultState = {
  pending : {
    [ExportContext.deviceTypes] : false,
    [ExportContext.aempConfig]  : false
  },
  entity      : null,
  isExporting : false,
  isComplete  : false,
  isError     : false,
  show        : false,
  exportLimit : 0
};

// defines the initial state for the reducer ...
export const initialState: ExportState = {
  current : {...defaultState}
};

// defines this reducers reducer functions ...
const reducers = {
  [EXPORT_REQUEST] : ({current}: ExportState, {entity, exportLimit}: ExportAction) => {
    current.pending[entity] = true;
    current.entity          = entity;
    current.isExporting     = true;
    current.show            = true;
    current.isComplete      = false;
    current.isError         = false;
    current.exportLimit     = exportLimit;
  },
  [EXPORT_RESPONSE] : ({current}: ExportState, {entity}: ExportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.isComplete      = true;
    current.show            = true;
    current.isExporting     = false;
    current.isError         = false;
    current.exportLimit     = 0;
  },
  [EXPORT_ERROR] : ({current}: ExportState, {entity}: ExportAction) => {
    current.pending[entity] = false;
    current.entity          = entity;
    current.isError         = true;
    current.show            = true;
    current.isComplete      = false;
    current.isExporting     = false;
    current.exportLimit     = 0;
  },
  [EXPORT_CONFIRMED] : ({current}: ExportState) => {
    current.show        = false;
  },
  [EXPORT_COMPLETED] : (draft: ExportState) => {
    draft.current = {...defaultState};
  }
};

export const isExporting = (pending: GenericObject, entity: ExportContext): boolean => {
  return pending[entity] === true;
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: ExportState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
